import {BufferGeometryUtils, VectorUtils} from "../common/Utils.js";
import * as THREE from "three";

class Wall {
    constructor(wallMesh, additionalMesh, holdsMesh, routes, holds, wallMetadata) {
        this.wallMesh = wallMesh;
        this.additionalMesh = additionalMesh;
        this.holdsMesh = holdsMesh;
        this.metadata = wallMetadata;
        try {
            this.customData = JSON.parse(wallMetadata["customData"]);
        } catch {
            this.customData = {};
        }
        this.routes = routes;
        this.holds = holds;
    }

    static fromServerData(wallMesh, additionalMesh, holdsMesh, routesData, wallMetadata) {
        const routes = [];
        const holds = new Map();

        holdsMesh.traverse(child => {
            if (child.type === "Mesh") {
                holds.set(child.name, new Hold(child, child.name));
            }
        });

        for (let routeData of routesData) {
            const routeHolds = [];
            for (let holdName of routeData["holds"]) {
                if (!holds.has(holdName))
                    throw new Error(`Route contains unknown hold "${holdName}".`)
                routeHolds.push(holds.get(holdName));
            }
            // Holds might get modified and this would become stale.
            delete routeData.holds;
            routes.push(new Route(routeHolds, routeData));
        }
        return new Wall(wallMesh, additionalMesh, holdsMesh, routes, holds, wallMetadata);
    }

    shallowClone() {
        return new Wall(
            this.wallMesh,
            this.additionalMesh,
            this.holdsMesh,
            this.routes,
            this.holds,
            JSON.parse(JSON.stringify(this.metadata)),
        );
    }

    precomputeViewingData() {
        for (let route of this.routes) {
            route.precomputeViewingData(this.wallMesh);
        }
    }

    unassignedHolds() {
        const unassigned = []
        for (let hold of this.holds.values()) {
            if (hold.route === null) {
                unassigned.push(hold);
            }
        }
        return unassigned;
    }
}

class Hold {
    constructor(holdMesh, name) {
        this.holdMesh = holdMesh;
        this.name = name;
        this._center = null;

        // Will be set by Route.
        this.route = null;
    }

    getCenter() {
        if (this._center === null) {
            this._center = BufferGeometryUtils.meshCenter(this.holdMesh);
        }
        return this._center;
    }
}

class Route {
    constructor(holds, metadata) {
        this.holds = holds;
        this.metadata = metadata;
        this.name = metadata["name"];
        this.id = metadata["id"];
        this.builder = metadata["builder"];
        this.sector = metadata["sector"];
        this.difficulty = metadata["difficulty"];
        this.color = metadata["color"];
        this.creationDateUtc = metadata["creationDateUtc"];

        this.center = this.metadata["center"] || null;
        this.viewingDirection = this.metadata["viewing_direction"] || null;
        this.viewingPoint = this.metadata["viewing_point"] || null;

        for (let hold of holds) {
            hold.route = this;
        }
    }

    getFullMetadata() {
        const holdsMetadata = this.holds.map(hold => hold.name);
        return {
            ...this.metadata,
            holds: holdsMetadata,
        };
    }

    static getCreationDateString(route) {
        return route.creationDateUtc === null ?
            null :
            new Date(route.creationDateUtc * 1000).toDateString();
    }

    static getColorHexString(route) {
        const color = route.color;
        if (color === null)
            return null;
        if (color.match(/^[0-9a-f]{6}$/)) {
            return "#" + color;
        }
        return null;
    }

    removeHold(hold) {
        console.assert(hold.route === this);
        const holdIdx = this.holds.indexOf(hold);
        console.assert(holdIdx >= 0);
        this.holds.splice(holdIdx, 1);
        hold.route = null;
    }

    addHold(hold) {
        console.assert(this.holds.indexOf(hold) === -1);
        this.holds.push(hold);
        hold.route = this;
    }

    precomputeViewingData(wallMesh) {
        this.getCenter();
        this.getViewingPoint(wallMesh);
        this.getViewingDirection(wallMesh);
    }

    getCenter() {
        if (this.center !== null)
            return this.center.clone();

        const holdPositions = this.holds.map(hold => hold.getCenter());
        this.center = VectorUtils.averageVectors3(holdPositions);
        return this.center.clone();
    }

    getViewingPoint(wallMesh) {
        if (this.viewingPoint !== null)
            return this.viewingPoint.clone();

        const center = this.getCenter();
        const direction = this.getViewingDirection(wallMesh);
        // TODO: we should probably not just add a constant, but take into account the bounding box of the route
        this.viewingPoint = center.add(direction.multiplyScalar(-4));
        return this.viewingPoint.clone();
    }

    getViewingDirection(wallMesh) {
        if (this.viewingDirection !== null)
            return this.viewingDirection.clone();

        const normalCandidates = [];
        for (let hold of this.holds) {
            const normal = this._getHoldWallNormal(wallMesh, hold);
            if (normal !== null) {
                normalCandidates.push(normal);
            }
        }
        if (normalCandidates.length === 0)
            throw Error(`None of the holds of ${this.name} could be projected on the wall mesh.`)
        const normal = VectorUtils.averageVectors3(normalCandidates);
        this.viewingDirection = normal.multiplyScalar(-1).normalize();
        return this.viewingDirection.clone();
    }

    _getHoldWallNormal(wallMesh, hold) {
        const center = BufferGeometryUtils.meshCenter(hold.holdMesh);
        let bestDistance = null;
        let bestNormal = new THREE.Vector3();
        for (let triangle of BufferGeometryUtils.triangleIterator(wallMesh)) {
            if (!triangle.containsPoint(center))
                continue;
            const centerProjection = new THREE.Vector3();
            triangle.closestPointToPoint(center, centerProjection);
            const distance = centerProjection.distanceToSquared(center);
            if (bestDistance === null || distance < bestDistance) {
                bestDistance = distance;
                triangle.getNormal(bestNormal);
            }
        }
        return bestDistance === null ? null : bestNormal;
    }
}

export {Wall, Hold, Route};